import { useMemo, useCallback } from 'react';
import { useMarketDataContext } from '../context/MarketDataContext.utils';

export function useMarketData() {
  const { state } = useMarketDataContext();

  const nameFor = useCallback(
    (positionId: string): string | undefined => {
      // Strip the trailing numeric suffix ("R01" → "R", "B:A:01" → "B:A"),
      // then take the first separator-delimited segment as the location key.
      // The optional middle segment ("A" in "B:A:01") is authoring-only — it
      // lets staff number amenities independently of vendors on the same
      // street. The amenity kind itself is on Position.type.
      const stripped = positionId.replace(/[:\-_]?\d+$/, '');
      const key = stripped.split(/[:\-_]/)[0] ?? '';
      return key ? state.names[key] : undefined;
    },
    [state.names],
  );

  const filteredVendors = useMemo(() => {
    const validUids = new Set(state.positions.map((p) => p.uid));
    let result = state.vendors.filter((v) => validUids.has(v.positionUid));

    result = result.filter((v) => state.filters.categoryIds.has(v.categoryId));

    if (state.filters.search.trim()) {
      const q = state.filters.search.toLowerCase();
      const categoryNameFor = (categoryId: string): string =>
        state.categories.find((c) => c.id === categoryId)?.name ?? '';
      result = result.filter(
        (v) =>
          v.name.toLowerCase().includes(q) ||
          v.description.toLowerCase().includes(q) ||
          (nameFor(v.positionId) ?? '').toLowerCase().includes(q) ||
          v.positionId.toLowerCase().includes(q) ||
          v.categoryId.toLowerCase().includes(q) ||
          categoryNameFor(v.categoryId).toLowerCase().includes(q) ||
          (v.keywords ?? []).some((k) => k.toLowerCase().includes(q)),
      );
    }

    return result;
  }, [state.vendors, state.positions, state.filters, nameFor]);

  return {
    vendors: state.vendors,
    filteredVendors,
    positions: state.positions,
    categories: state.categories,
    names: state.names,
    nameFor,
    filters: state.filters,
  };
}
